import { CouponServiceAPI } from '@API/data-services/coupon-service'
import { RootState } from '@AppState/redux-store'
import { WithPermissions } from '@Components/common/with-permissions'
import { Routes } from '@Components/main-app/types'
import { LoadingRows } from '@Components/util/loading-rows'
import { formatDateToMonthDayYearString, formatDateToTimeZone } from '@Components/util/utils'
import { AuthScopes, CouponPromotionDetailsWithCounts } from '@Generated/back-office-client/src'
import {
  Button,
  Flex,
  Link as ThemedLink,
  Section,
  Skeleton,
  Table,
  Text,
  Heading,
} from '@radix-ui/themes'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import './styles.scss'
import { CheckCircledIcon, ValueNoneIcon } from '@radix-ui/react-icons'
import { SideDrawer } from '@Components/common/side-drawer'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config'

export const CouponsAdmin = () => {
  const [autoRefresh, setAutoRefresh] = useState(false)
  const { activeCustomerId, activeCustomerMetadata } = useSelector(
    (state: RootState) => state.users,
  )
  const nav = useNavigate()
  const [selectedPromotionDetails, setSelectedPromotionDetails] =
    useState<CouponPromotionDetailsWithCounts | null>(null)

  const { data: couponPromotionData, isLoading } = useQuery({
    queryKey: [`${activeCustomerId}-coupon-promotions`],
    refetchOnMount: 'always',
    refetchInterval: autoRefresh ? 15000 : false,
    queryFn: async () => {
      return CouponServiceAPI.getCouponPromotions()
    },
  })

  useEffect(() => {
    if (couponPromotionData) {
      const hasNotReady = couponPromotionData.find((promotion) => !promotion.ready)
      if (hasNotReady) {
        setAutoRefresh(true)
      } else {
        setAutoRefresh(false)
      }
    }
  }, [couponPromotionData])

  const { data: couponPromotionDetails } = useQuery({
    queryKey: [`${activeCustomerId}-coupon-promotion-${selectedPromotionDetails?.id}`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      return CouponServiceAPI.getCouponPromotion(selectedPromotionDetails!.id)
    },
    enabled: !!selectedPromotionDetails,
  })

  const mapCouponPromotions = useCallback(() => {
    if (!couponPromotionData) {
      return []
    }

    return couponPromotionData.map((couponPromotion) => {
      const totalCoupons = couponPromotion.redeemedCount + couponPromotion.notRedeemedCount

      return (
        <Table.Row key={couponPromotion.id}>
          <Table.RowHeaderCell>
            <ThemedLink
              className="details-link"
              onClick={() => setSelectedPromotionDetails(couponPromotion)}>
              {couponPromotion.id}
            </ThemedLink>
          </Table.RowHeaderCell>
          <Table.Cell>{couponPromotion.name}</Table.Cell>
          <Table.Cell>{couponPromotion.redeemedCount}</Table.Cell>
          <Table.Cell>{couponPromotion.assignedCount}</Table.Cell>
          <Table.Cell>{totalCoupons - couponPromotion.assignedCount}</Table.Cell>
          <Table.Cell>{totalCoupons}</Table.Cell>
          <Table.Cell>
            {couponPromotion.ready ? (
              <CheckCircledIcon className="ready" />
            ) : (
              <ValueNoneIcon className="not-ready" />
            )}
          </Table.Cell>
          <Table.Cell>
            {formatDateToMonthDayYearString(
              couponPromotion.startDate,
              activeCustomerMetadata?.timezone,
            )}
          </Table.Cell>
          <Table.Cell>
            {formatDateToMonthDayYearString(
              couponPromotion.endDate,
              activeCustomerMetadata?.timezone,
            )}
          </Table.Cell>
        </Table.Row>
      )
    })
  }, [activeCustomerMetadata?.timezone, couponPromotionData])

  return (
    <>
      <Section className="section-width">
        <Flex direction="column" gap="3">
          <WithPermissions requiredPermissions={[AuthScopes.writecoupons]}>
            <Flex justify="end">
              <Button onClick={() => nav(Routes.CREATE_COUPON_PROMOTIONS)}>New</Button>
            </Flex>
          </WithPermissions>

          <Skeleton loading={isLoading}>
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Id</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Coupon Promotion Name</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Redeemed</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Assigned</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Unallocated</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Total</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Ready</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Start Date</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>End Date</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>{isLoading ? <LoadingRows /> : mapCouponPromotions()}</Table.Body>
            </Table.Root>
          </Skeleton>
        </Flex>
      </Section>
      {couponPromotionDetails ? (
        <SideDrawer onClose={() => setSelectedPromotionDetails(null)}>
          <Heading as="h3" size="4">
            {couponPromotionDetails.name}
          </Heading>
          <Text>{couponPromotionDetails.ready}</Text>
          <Text>
            Start Date:{' '}
            {formatDateToTimeZone(
              couponPromotionDetails.startDate,
              activeCustomerMetadata?.timezone,
            )}
          </Text>
          <Text>
            End Date:{' '}
            {formatDateToTimeZone(couponPromotionDetails.endDate, activeCustomerMetadata?.timezone)}
          </Text>
          <Flex className="coupon-description">
            <pre>{couponPromotionDetails.description}</pre>
          </Flex>

          <img
            src={`data:image;base64,${couponPromotionDetails.templateImage}`}
            alt="coupon-image"></img>
        </SideDrawer>
      ) : null}
    </>
  )
}
